import { FeatureGateBannerQuotaLevel } from "@/components/banners/feature-gate-banner/feature-gate-banner.types";
import {
  UpsellModalView,
  type UpsellPlanActions,
} from "@/components/modal/upsell/upsell-modal-view";
import type { PaywallPlanCardDefinition } from "@/components/modal/paywall/paywall-modal-components";
import {
  getProPlanFeatures,
  PROACTIVE_COMPARISON_PLAN_FEATURES,
} from "@/components/modal/paywall/plan-features";
import { GatedFeature, type FeatureLimit, type FeatureLimits } from "@/store/feature-gates/types";
import { useAppStore } from "@/store/utils/hooks";
import { useEffect, useState } from "react";
import { trackEvent } from "@/domains/metrics/segment";
import {
  type FeatureGateLevel,
  TrackedEvent,
  PaywallContext,
} from "@/domains/metrics/tracked-event";
import {
  buildPaywallEventPayload,
  buildSubscriptionPlanMetricsPayload,
} from "@/domains/metrics/monetization";
import { BillingStrategyEnum } from "@/store/pages/SettingsSubscriptionManagementPageStore/types";
import { useLocation } from "react-router-dom";
import { subscriptionModule } from "@/modules/subscriptions";
import type { AppStore } from "@/store";
import { SubscriptionPlanKind, SubscriptionPlanVariant } from "@/store/subscriptions/types";
import { startProactiveCheckout } from "@/components/modal/proactive-upsell/proactive-checkout";
import {
  buildProactivePlanCard,
  getProactivePlanActionCopy,
  PROACTIVE_MONEY_BACK_GUARANTEE_NOTE,
} from "@/components/modal/proactive-upsell/proactive-plan";
import { ProactiveUpsellModalView } from "@/components/modal/proactive-upsell/proactive-upsell-modal-view";
import { observer } from "mobx-react-lite";
import { CommercialPlanSelectorModal } from "@/components/commercial-plan-selector";
import {
  CommercialPlanReviewModal,
  getCommercialUsageLimitCopy,
} from "@/components/modal/commercial-usage-limit";
import {
  CommercialMeterKey,
  CommercialPlanKey,
  CommercialUsageRecoveryAction,
  getCompatibleCommercialCatalog,
  isCommercialRolloutActive,
  type CommercialPlanDefinition,
  type CommercialMeterKey as CommercialMeterKeyType,
} from "@/modules/commercial-plans";
import { SettingsTabId } from "@/store/pages/SettingsPageStore";

interface UpsellModalProps {
  isOpen: boolean;
  onClose: () => void;
  gatedFeature?: GatedFeature;
  level?: FeatureGateLevel;
  titleOverride?: string;
}

interface UpsellModalConfig {
  title: string;
  contextDetail?: string;
}

interface PdfUsage {
  used: number;
  limit: number;
}

interface NativeChatPlanFeatureLabels {
  proChatMessageFeatureLabel: string;
}

const FALLBACK_NATIVE_CHAT_PRO_MONTHLY_LIMIT = 250;

const getCommercialMeterKey = (
  feature: GatedFeature | undefined
): CommercialMeterKeyType | null => {
  if (feature === GatedFeature.NoteCreate) {
    return CommercialMeterKey.NewNotes;
  }

  if (feature === GatedFeature.ChatMessageSend || feature === GatedFeature.NativeChatMessageSend) {
    return CommercialMeterKey.UserRequests;
  }

  return null;
};

const getNativeChatMonthlyLimit = ({
  limits,
  limitKeys,
  fallbackLimit,
}: {
  limits: FeatureLimits | undefined;
  limitKeys: FeatureLimit[];
  fallbackLimit: number | null;
}): number | null => {
  if (!limits) {
    return fallbackLimit;
  }

  for (const limitKey of limitKeys) {
    if (Object.prototype.hasOwnProperty.call(limits, limitKey)) {
      return limits[limitKey] ?? null;
    }
  }

  return fallbackLimit;
};

const formatNativeChatMonthlyLimitLabel = ({ limit }: { limit: number | null }): string => {
  return limit === null
    ? "Unlimited Mem Agent chat messages"
    : `${limit} Mem Agent chat messages per month`;
};

const getNativeChatPlanFeatureLabels = (store: AppStore) => {
  const nativeChatConfig = store.platformInfo.getFeatureGateConfig(
    GatedFeature.NativeChatMessageSend
  );

  return {
    proChatMessageFeatureLabel: formatNativeChatMonthlyLimitLabel({
      limit: getNativeChatMonthlyLimit({
        limits: nativeChatConfig?.limits,
        limitKeys: ["pro", "paid"],
        fallbackLimit: FALLBACK_NATIVE_CHAT_PRO_MONTHLY_LIMIT,
      }),
    }),
  };
};

const FREE_TIER_TRIAL_PLAN_CHOICE_SUBTITLE =
  "Choose Pro for $12/month, or try Proactive free for 7 days with everything in Pro plus Mem Agent.";
const FREE_TIER_STANDARD_PRICE_PLAN_CHOICE_SUBTITLE =
  "Choose Pro for $12/month, or upgrade to Proactive with everything in Pro plus Mem Agent.";

const prioritizeFeatures = ({
  features,
  priorityFeatures,
}: {
  features: string[];
  priorityFeatures: string[];
}): string[] => [...new Set([...priorityFeatures, ...features])];

const getPaywallProPlanFeatures = ({
  feature,
  nativeChatPlanFeatureLabels,
}: {
  feature?: GatedFeature;
  nativeChatPlanFeatureLabels?: NativeChatPlanFeatureLabels;
}): string[] => {
  switch (feature) {
    case GatedFeature.NoteCreate:
      return prioritizeFeatures({
        features: getProPlanFeatures(),
        priorityFeatures: ["Unlimited notes"],
      });
    case GatedFeature.ChatMessageSend:
      return prioritizeFeatures({
        features: getProPlanFeatures(),
        priorityFeatures: ["Unlimited Classic Chat messages"],
      });
    case GatedFeature.NativeChatMessageSend:
      return prioritizeFeatures({
        features: getProPlanFeatures(),
        priorityFeatures: [
          nativeChatPlanFeatureLabels?.proChatMessageFeatureLabel ??
            formatNativeChatMonthlyLimitLabel({ limit: FALLBACK_NATIVE_CHAT_PRO_MONTHLY_LIMIT }),
        ],
      });
    case GatedFeature.DarkMode:
      return prioritizeFeatures({
        features: getProPlanFeatures(),
        priorityFeatures: ["Dark mode"],
      });
    case GatedFeature.ContentUnderstandingPdfPage:
      return prioritizeFeatures({
        features: getProPlanFeatures(),
        priorityFeatures: ["Unlimited PDFs in search and chat"],
      });
    default:
      return getProPlanFeatures();
  }
};

const buildProPlanCard = ({
  feature,
  nativeChatPlanFeatureLabels,
}: {
  feature?: GatedFeature;
  nativeChatPlanFeatureLabels?: NativeChatPlanFeatureLabels;
}): PaywallPlanCardDefinition =>
  ({
    id: "pro",
    title: "Pro",
    price: "$12/month",
    features: getPaywallProPlanFeatures({ feature, nativeChatPlanFeatureLabels }),
    tone: "default",
    checkboxes: "emphasis",
  }) satisfies PaywallPlanCardDefinition;

const getFreeTierPlanCards = ({
  feature,
  isTrialEligible,
  nativeChatPlanFeatureLabels,
  proactivePrice,
  pricingError,
}: {
  feature?: GatedFeature;
  isTrialEligible: boolean;
  nativeChatPlanFeatureLabels?: NativeChatPlanFeatureLabels;
  proactivePrice?: string;
  pricingError?: string;
}): PaywallPlanCardDefinition[] => {
  const proactivePlanCard = {
    ...buildProactivePlanCard({
      badgeLabel: "Recommended",
      isTrialEligible,
      proactivePrice,
      pricingError,
    }),
    features: PROACTIVE_COMPARISON_PLAN_FEATURES,
  } satisfies PaywallPlanCardDefinition;

  const proPlanCard = buildProPlanCard({
    feature,
    nativeChatPlanFeatureLabels,
  });

  return [proPlanCard, proactivePlanCard];
};

const getUpsellModalConfig = (
  level: FeatureGateLevel | undefined,
  feature?: GatedFeature,
  pdfUsage?: PdfUsage
): UpsellModalConfig => {
  const quotaLevel = level ?? FeatureGateBannerQuotaLevel.Exceeded;

  switch (feature) {
    case GatedFeature.NoteCreate:
      return {
        title:
          quotaLevel === FeatureGateBannerQuotaLevel.Exceeded
            ? "You've reached your monthly new-note limit"
            : "You're nearing your monthly new-note limit",
      };
    case GatedFeature.ChatMessageSend:
      return {
        title:
          quotaLevel === FeatureGateBannerQuotaLevel.Exceeded
            ? "You've reached your monthly request limit"
            : "You're nearing your monthly request limit",
      };
    case GatedFeature.NativeChatMessageSend:
      return {
        title:
          quotaLevel === FeatureGateBannerQuotaLevel.Exceeded
            ? "You've reached your monthly Agent request limit"
            : "You're nearing your monthly Agent request limit",
      };
    case GatedFeature.DarkMode:
      return {
        title: "Unlock Dark mode",
      };
    case GatedFeature.ContentUnderstandingPdfPage: {
      const used = pdfUsage?.used ?? 0;
      const limit = pdfUsage?.limit ?? 0;
      const contextDetail =
        quotaLevel === FeatureGateBannerQuotaLevel.Exceeded
          ? `Mem has understood ${used} of your ${limit} PDF pages this month.`
          : `Mem has understood ${used} of your ${limit} PDF pages this month\u2014making them searchable and available in chat. Uploading more would exceed your limit.`;
      return {
        title: "Unlock unlimited PDF search and chat",
        contextDetail,
      };
    }
    default:
      return {
        title:
          quotaLevel === FeatureGateBannerQuotaLevel.Exceeded
            ? "You've reached your monthly usage limits"
            : "You're nearing your monthly usage limits",
      };
  }
};

export const UpsellModal = observer(
  ({ isOpen, onClose, gatedFeature, level, titleOverride }: UpsellModalProps) => {
    const { pageStore, store } = useAppStore();
    const subscriptionStore = store.subscriptions;
    const context: PaywallContext | undefined = subscriptionStore.upsellModalContext;
    const pdfUsage =
      gatedFeature === GatedFeature.ContentUnderstandingPdfPage
        ? store.featureGates.checkGate(GatedFeature.ContentUnderstandingPdfPage)
        : undefined;
    const isNativeChatPaywall = gatedFeature === GatedFeature.NativeChatMessageSend;
    const isFreeTier =
      subscriptionStore.effectiveDisplayPlanKind === SubscriptionPlanKind.FREE ||
      (subscriptionStore.featureAccess?.isPaid !== true &&
        subscriptionStore.billingSubscription?.isPaid !== true);
    const nativeChatPlanFeatureLabels =
      isFreeTier && isNativeChatPaywall ? getNativeChatPlanFeatureLabels(store) : undefined;
    const proactivePricingAssignment = subscriptionStore.proactivePricingAssignment;
    const isProactiveTrialEligible = subscriptionStore.isProactiveUpsellTrialEligible;
    const proactivePricingError = subscriptionStore.proactivePricingAssignmentError;
    const proactivePricingIsLoading =
      subscriptionStore.proactivePricingAssignmentIsLoading ||
      (!proactivePricingAssignment && proactivePricingError === undefined);
    const proactivePrice = proactivePricingAssignment?.display_price;
    const { title: contextualTitle, contextDetail } = getUpsellModalConfig(
      level,
      gatedFeature,
      pdfUsage
    );
    const title = titleOverride ?? contextualTitle;
    const [checkoutPlanKind, setCheckoutPlanKind] = useState<SubscriptionPlanKind | null>(null);
    const [commercialCheckoutPlanKey, setCommercialCheckoutPlanKey] = useState<string | null>(null);
    const [commercialPlanMode, setCommercialPlanMode] = useState<"all" | "focused">("focused");
    const location = useLocation();
    const commercialPlanSnapshot = store.platformInfo.commercialPlanSnapshot;
    const commercialUsageLimitRejection = subscriptionStore.commercialUsageLimitRejection ?? null;
    const commercialCatalog = getCompatibleCommercialCatalog({
      catalog: store.platformInfo.commercialCatalog,
      snapshot: commercialPlanSnapshot,
    });
    const commercialRolloutIsActive = isCommercialRolloutActive({
      catalog: store.platformInfo.commercialCatalog,
      snapshot: commercialPlanSnapshot,
    });
    const commercialMeterKey =
      commercialUsageLimitRejection?.meterKey ?? getCommercialMeterKey(gatedFeature);
    const commercialLimitWasReached =
      commercialUsageLimitRejection !== null ||
      (level ?? FeatureGateBannerQuotaLevel.Exceeded) === FeatureGateBannerQuotaLevel.Exceeded;

    useEffect(() => {
      if (!isOpen) return;

      trackEvent(
        TrackedEvent.PaywallView,
        buildPaywallEventPayload({
          store,
          context,
          feature: gatedFeature,
          level,
        })
      );
    }, [context, gatedFeature, isOpen, level, store]);

    useEffect(() => {
      if (!isOpen || commercialRolloutIsActive) return;

      subscriptionStore.fetchProactivePricingAssignment().catch(() => undefined);
    }, [commercialRolloutIsActive, isOpen, subscriptionStore]);

    useEffect(() => {
      if (isOpen) {
        setCommercialPlanMode("focused");
      }
    }, [isOpen]);

    const retryPricingAssignment = async () => {
      try {
        await subscriptionStore.fetchProactivePricingAssignment();
      } catch {
        // The visible pricing error is owned by the subscription store.
      }
    };

    const handleUpgrade = async (targetPlanKind: SubscriptionPlanKind) => {
      if (checkoutPlanKind !== null) return;

      setCheckoutPlanKind(targetPlanKind);
      const targetPlanVariant =
        targetPlanKind === SubscriptionPlanKind.PROACTIVE
          ? SubscriptionPlanVariant.STANDARD
          : undefined;

      trackEvent(TrackedEvent.PaywallUpgradeClick, {
        ...buildPaywallEventPayload({
          store,
          context,
          feature: gatedFeature,
          level,
        }),
        ...buildSubscriptionPlanMetricsPayload({
          store,
          targetPlanKind,
          targetPlanVariant,
        }),
      });

      try {
        if (targetPlanKind === SubscriptionPlanKind.PROACTIVE) {
          await startProactiveCheckout({ location, store });
        } else {
          await subscriptionModule.createCheckoutSessionAndNavigate({
            billingStrategy: BillingStrategyEnum.MONTHLY,
            location,
            planKind: SubscriptionPlanKind.PRO,
            store,
          });
        }
      } finally {
        setCheckoutPlanKind(null);
        onClose();
      }
    };

    const handleClose = () => {
      trackEvent(
        TrackedEvent.PaywallCancelClick,
        buildPaywallEventPayload({
          store,
          context,
          feature: gatedFeature,
          level,
        })
      );
      onClose();
    };

    const handleReviewCommercialPlans = () => {
      trackEvent(
        TrackedEvent.PaywallUpgradeClick,
        buildPaywallEventPayload({
          store,
          context,
          feature: gatedFeature,
          level,
          commercialUpgradeAction: "review_plans",
        })
      );
      onClose();
      store.settingsModal.open(SettingsTabId.SubscriptionManagementPage);
    };

    const openCommercialPlanManagement = (plan: CommercialPlanDefinition) => {
      pageStore.settingsSubscriptionManagementPage.selectCommercialPlan(plan.key);
      onClose();
      store.settingsModal.open(SettingsTabId.SubscriptionManagementPage);
    };

    const handleChooseCommercialPlan = async (plan: CommercialPlanDefinition) => {
      if (!commercialPlanSnapshot || commercialCheckoutPlanKey !== null) {
        return;
      }

      if (plan.key === CommercialPlanKey.Mem || !plan.checkoutAvailable) {
        return;
      }

      trackEvent(
        TrackedEvent.PaywallUpgradeClick,
        buildPaywallEventPayload({
          store,
          context,
          feature: gatedFeature,
          level,
          targetCommercialPlanKey: plan.key,
          commercialUpgradeAction: "select_plan",
        })
      );

      if (
        commercialPlanSnapshot.currentPlan.isLegacy ||
        commercialPlanSnapshot.currentPlan.key !== CommercialPlanKey.Mem
      ) {
        openCommercialPlanManagement(plan);
        return;
      }

      setCommercialCheckoutPlanKey(plan.key);
      try {
        const checkoutResult = await subscriptionModule.createCheckoutSessionAndNavigate({
          billingStrategy: BillingStrategyEnum.MONTHLY,
          commercialPlanKey: plan.key,
          extraUsageBlocks: 0,
          location,
          store,
        });
        const upgradeFlowIsWaitingForCheckoutReturn =
          checkoutResult.didStartExternalNavigation && subscriptionStore.upgradePlanFlowIsActive;
        if (!upgradeFlowIsWaitingForCheckoutReturn) {
          onClose();
        }
      } finally {
        setCommercialCheckoutPlanKey(null);
      }
    };

    if (commercialRolloutIsActive) {
      if (commercialCatalog && commercialPlanSnapshot) {
        const limitCopy =
          commercialMeterKey && commercialLimitWasReached
            ? getCommercialUsageLimitCopy(commercialMeterKey, {
                accepted: commercialUsageLimitRejection?.accepted,
              })
            : null;
        const modalDetail = limitCopy ? `${limitCopy.body} ${limitCopy.recovery}` : contextDetail;
        const canReviewExtraUsage =
          commercialPlanSnapshot.blockPolicy?.available === true &&
          (commercialUsageLimitRejection === null ||
            commercialUsageLimitRejection.recoveryActions.includes(
              CommercialUsageRecoveryAction.ReviewExtraUsage
            ));

        return (
          <CommercialPlanSelectorModal
            actionIsLoading={commercialCheckoutPlanKey !== null}
            catalog={commercialCatalog}
            detail={modalDetail}
            isOpen={isOpen}
            meterKey={commercialMeterKey}
            mode={commercialPlanMode}
            onChoosePlan={plan => void handleChooseCommercialPlan(plan)}
            onClose={handleClose}
            onSecondaryAction={
              canReviewExtraUsage
                ? () => {
                    const currentPlan = commercialCatalog.plans.find(
                      plan => plan.key === commercialPlanSnapshot.currentPlan.key
                    );
                    if (currentPlan) {
                      openCommercialPlanManagement(currentPlan);
                    }
                  }
                : undefined
            }
            onShowAllPlans={
              commercialPlanMode === "focused" ? () => setCommercialPlanMode("all") : undefined
            }
            secondaryActionLabel={canReviewExtraUsage ? "Review extra usage" : undefined}
            snapshot={commercialPlanSnapshot}
            title={limitCopy?.title ?? title}
          />
        );
      }

      return (
        <CommercialPlanReviewModal
          detail={contextDetail}
          isOpen={isOpen}
          onClose={handleClose}
          onReviewPlans={handleReviewCommercialPlans}
          plansAreAvailable={commercialCatalog !== null}
          title={title}
        />
      );
    }

    if (!isFreeTier) {
      return (
        <ProactiveUpsellModalView
          isOpen={isOpen}
          isTrialEligible={isProactiveTrialEligible}
          title={title}
          onClose={handleClose}
          onSubscribe={() => handleUpgrade(SubscriptionPlanKind.PROACTIVE)}
          onRetryPricing={retryPricingAssignment}
          isSubscribing={checkoutPlanKind === SubscriptionPlanKind.PROACTIVE}
          proactivePrice={proactivePrice}
          proactivePriceCents={proactivePricingAssignment?.display_price_cents}
          pricingIsLoading={proactivePricingIsLoading}
          pricingError={proactivePricingError}
        />
      );
    }

    const proactiveActionCopy = getProactivePlanActionCopy({
      isTrialEligible: isProactiveTrialEligible,
      proactivePrice,
      pricingError: proactivePricingError,
      pricingIsLoading: proactivePricingIsLoading,
    });
    const planChoiceSubtitle = isProactiveTrialEligible
      ? FREE_TIER_TRIAL_PLAN_CHOICE_SUBTITLE
      : FREE_TIER_STANDARD_PRICE_PLAN_CHOICE_SUBTITLE;
    const subtitle = [contextDetail, planChoiceSubtitle].filter(Boolean).join(" ");
    const planCards = getFreeTierPlanCards({
      feature: gatedFeature,
      isTrialEligible: isProactiveTrialEligible,
      nativeChatPlanFeatureLabels,
      proactivePrice,
      pricingError: proactivePricingError,
    });
    const planActions: UpsellPlanActions = {
      pro: {
        buttonLabel: "Upgrade to Pro",
        isDisabled: checkoutPlanKind === SubscriptionPlanKind.PROACTIVE,
        isLoading: checkoutPlanKind === SubscriptionPlanKind.PRO,
        note: "Billed monthly. Cancel anytime.",
        onClick: () => handleUpgrade(SubscriptionPlanKind.PRO),
        tone: "default",
      },
      proactive: {
        buttonLabel: proactiveActionCopy.buttonLabel,
        isDisabled: checkoutPlanKind === SubscriptionPlanKind.PRO,
        isLoading: proactivePricingIsLoading || checkoutPlanKind === SubscriptionPlanKind.PROACTIVE,
        note: proactiveActionCopy.pricingUnavailable
          ? undefined
          : PROACTIVE_MONEY_BACK_GUARANTEE_NOTE,
        onClick: proactiveActionCopy.pricingUnavailable
          ? retryPricingAssignment
          : () => handleUpgrade(SubscriptionPlanKind.PROACTIVE),
      },
    };

    return (
      <UpsellModalView
        isOpen={isOpen}
        onClose={handleClose}
        title={title}
        subtitle={subtitle}
        planCards={planCards}
        planActions={planActions}
      />
    );
  }
);
